import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { BookMarkedIcon } from 'lucide-react';

import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { languageByCountryCode } from '@/shared/languageCodes';
import { Brain } from '@/shared/types/brain';
import { Collection } from '@/shared/types/collections';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';
import { formatLanguageLabel } from '@/util/languageOptions';

import ActionsCell from './ActionsCell';
import { TypeCountsCell } from './TypeCountsCell';

export const columns = ({ brains }: { brains: Brain[] }): ColumnDef<Collection>[] => [
  {
    id: 'select',
    cell: () => {
      return (
        <div className="flex items-center justify-center">
          <BookMarkedIcon className="text-muted-foreground" size={16} />
        </div>
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>{t('common.title')}</SortableHeader>,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-3">
          <span className="min-w-0 truncate font-medium">{row.original.name}</span>
          <RowActionIndicator action="open" />
        </div>
      );
    },
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
  },
  {
    id: 'content',
    header: () => t('common.content'),
    cell: (info) => (
      <TypeCountsCell
        collectionId={info.row.original.collection_id}
        content={info.row.original.content}
      />
    ),
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'ai_agents',
    header: () => t('common.ai_agents'),
    cell: (info) => {
      const collection = info.row.original;
      const collectionId = collection.collection_id;

      const hasMatchingBrain = brains?.some((brain) => brain.collection_id === collectionId);
      return (
        <Badge
          type="secondary"
          flavour={hasMatchingBrain ? 'successful' : 'neutral'}
          rounded={false}
        >
          {hasMatchingBrain ? t('collections.connected') : t('collections.not_connected')}
        </Badge>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'last_used',
    accessorKey: 'last_used_at',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_used')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const lastUsed = row.original.last_used_at;
      return lastUsed ? (
        <TimestampTooltip timestamp={lastUsed}>{timeFromNow(lastUsed)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'language',
    accessorFn: (row) => languageByCountryCode[row.language] ?? row.language,
    header: ({ column }) => <SortableHeader column={column}>{t('field.language')}</SortableHeader>,
    cell: ({ row }) => {
      const lang = row.original.language;
      const language = formatLanguageLabel(lang);
      return (
        <InfoTooltip content={language} side="top">
          <div className="flex items-center gap-2">
            <LanguageFlag code={lang} />
            <span className="truncate">{language}</span>
          </div>
        </InfoTooltip>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'fragment_count',
    accessorFn: (row) => row.fragment_count ?? 0,
    header: ({ column }) => (
      <SortableHeader column={column}>{t('collections.fragments_used')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const fragmentCount = row.original.fragment_count;
      return <div className="pr-4 font-mono">{fragmentCount ?? 0}</div>;
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', isNumeric: true },
  },
  {
    id: 'actions',
    header: () => '',
    cell: ({ row }) => {
      const item = row.original;
      return <ActionsCell item={item} />;
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
];
